import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Flex } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import FormButtons from './FormButtons';
import FormField from './FormField';
import { FORM_BUTTON_TYPES } from './formConstants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function Form(_ref) {
  var _ref2, _ref3, _submitButton$action;
  var id = _ref.id,
    _ref$elements = _ref.elements,
    formStyles = _ref$elements.formStyles,
    fieldsContainerStyles = _ref$elements.fieldsContainerStyles,
    buttonsContainerStyles = _ref$elements.buttonsContainerStyles,
    fields = _ref$elements.fields,
    buttons = _ref$elements.buttons,
    bottomFields = _ref$elements.bottomFields,
    onSubmitAction = _ref$elements.onSubmitAction,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? () => {} : _ref$onChange,
    errorsOrder = _ref.errorsOrder,
    defaultValues = _ref.defaultValues,
    validationSchema = _ref.validationSchema,
    getFormState = _ref.getFormState,
    defaultErrors = _ref.defaultErrors,
    resetForm = _ref.resetForm,
    testid = _ref.testid,
    _ref$autoComplete = _ref.autoComplete,
    autoComplete = _ref$autoComplete === void 0 ? 'on' : _ref$autoComplete,
    _ref$fieldsetDisabled = _ref.fieldsetDisabled,
    fieldsetDisabled = _ref$fieldsetDisabled === void 0 ? false : _ref$fieldsetDisabled;
  var _useForm = useForm({
      mode: 'onBlur',
      defaultValues,
      resolver: validationSchema ? yupResolver(validationSchema) : undefined
    }),
    control = _useForm.control,
    handleSubmit = _useForm.handleSubmit,
    getValues = _useForm.getValues,
    errors = _useForm.formState.errors,
    setError = _useForm.setError,
    clearErrors = _useForm.clearErrors,
    setValue = _useForm.setValue,
    reset = _useForm.reset,
    resetField = _useForm.resetField,
    watch = _useForm.watch,
    trigger = _useForm.trigger;
  var watchValues = useWatch({
    control
  });
  var errorsRef = useRef({});
  var submitButton = buttons === null || buttons === void 0 ? void 0 : buttons.find(button => (button === null || button === void 0 ? void 0 : button.type) === FORM_BUTTON_TYPES.SUBMIT);
  var customSubmitButton = fields === null || fields === void 0 ? void 0 : fields.find(field => {
    var _field$props;
    return (field === null || field === void 0 || (_field$props = field.props) === null || _field$props === void 0 ? void 0 : _field$props.type) === FORM_BUTTON_TYPES.SUBMIT;
  });
  var noSubmitAction = () => console.error('Form does not have a submit button with an action set');
  var errorFn = errors => {
    var _firstErrorElement$sc;
    var firstElementName;
    if (errorsOrder) {
      firstElementName = errorsOrder.find(el => !!errors[el]);
    }
    if (!firstElementName) {
      firstElementName = Object.keys(errors)[0];
    }
    var firstErrorElement = document.querySelector("[data-fieldname=\"".concat(firstElementName, "\"]"));
    firstErrorElement === null || firstErrorElement === void 0 || (_firstErrorElement$sc = firstErrorElement.scrollIntoView) === null || _firstErrorElement$sc === void 0 || _firstErrorElement$sc.call(firstErrorElement, {
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    var subscription = watch(() => {
      if (onChange) {
        onChange(getValues());
      }
    });
    return () => subscription.unsubscribe();
  }, [watchValues]);
  useEffect(() => {
    if (defaultErrors) {
      for (var fieldName in defaultErrors) {
        setError(fieldName, defaultErrors[fieldName]);
      }
    }
  }, [defaultErrors]);
  useEffect(() => {
    return () => {
      getFormState === null || getFormState === void 0 || getFormState(getValues(), errorsRef.current);
    };
  }, [getFormState, getValues]);
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);
  useEffect(() => {
    if (resetForm) {
      var resetValues = {};
      for (var key in defaultValues) {
        resetValues[key] = '';
      }
      reset(resetValues);
    }
  }, [resetForm]);
  var handleTriggerValidation = useCallback(fieldsName => {
    trigger(fieldsName);
  }, [trigger]);
  var handleSetValue = useCallback((fieldName, value) => {
    setValue(fieldName, value);
  }, [setValue]);
  var handleResetField = useCallback((fieldName, options) => {
    resetField(fieldName, options);
  }, [resetField]);
  var handleSetError = useCallback((fieldName, error, config) => {
    setError(fieldName, error, config);
  }, [setError]);
  var handleClearErrors = useCallback(fieldName => {
    clearErrors(fieldName);
  }, [clearErrors]);
  var submitFn = (_ref2 = (_ref3 = (_submitButton$action = submitButton === null || submitButton === void 0 ? void 0 : submitButton.action) !== null && _submitButton$action !== void 0 ? _submitButton$action : customSubmitButton === null || customSubmitButton === void 0 ? void 0 : customSubmitButton.action) !== null && _ref3 !== void 0 ? _ref3 : onSubmitAction) !== null && _ref2 !== void 0 ? _ref2 : noSubmitAction;
  return _jsx("form", {
    id: id,
    onSubmit: handleSubmit(submitFn, errorFn),
    "data-testid": testid,
    autoComplete: autoComplete,
    children: _jsxs(Box, _objectSpread(_objectSpread({}, formStyles), {}, {
      children: [_jsx("fieldset", {
        disabled: fieldsetDisabled,
        style: styleFieldset(fieldsetDisabled),
        children: _jsx(Flex, _objectSpread(_objectSpread({}, _objectSpread(_objectSpread({}, defaultFieldsContainerStyles), fieldsContainerStyles)), {}, {
          children: fields.map(formField => {
            var _formField$id;
            return _jsx(FormField, {
              control: control,
              formField: formField,
              errors: errors,
              getValues: getValues,
              handleSetValue: handleSetValue,
              handleResetField: handleResetField,
              handleSetError: handleSetError,
              handleClearErrors: handleClearErrors,
              handleTriggerValidation: handleTriggerValidation,
              reset: reset
            }, "".concat(formField.name, "-").concat((_formField$id = formField.id) !== null && _formField$id !== void 0 ? _formField$id : ''));
          })
        }))
      }), buttons && _jsx(FormButtons, {
        buttonsContainerStyles: buttonsContainerStyles,
        buttons: buttons
      }), bottomFields && _jsx(Flex, _objectSpread(_objectSpread({}, _objectSpread(_objectSpread({}, defaultFieldsContainerStyles), fieldsContainerStyles)), {}, {
        children: bottomFields.map(formField => {
          var _formField$id2;
          return _jsx(FormField, {
            control: control,
            formField: formField,
            errors: errors,
            getValues: getValues,
            handleSetValue: handleSetValue,
            handleResetField: handleResetField,
            reset: reset
          }, "".concat(formField.name, "-").concat((_formField$id2 = formField.id) !== null && _formField$id2 !== void 0 ? _formField$id2 : ''));
        })
      }))]
    }))
  });
}
var defaultFieldsContainerStyles = {
  direction: 'column',
  height: 'auto',
  justifyContent: 'flex-start',
  marginBottom: 'var(--chakra-space-lg)'
};
var defaultFieldsetStyle = {
  border: '0',
  padding: '0.01em 0 0 0',
  margin: '0',
  minWidth: '0'
};
var styleFieldset = isDisabled => {
  return isDisabled ? _objectSpread(_objectSpread({}, {
    opacity: '0.4',
    pointerEvents: 'none'
  }), defaultFieldsetStyle) : defaultFieldsetStyle;
};